// src/pages/Exceptions.js
import React, { useState, useEffect, useCallback } from 'react';
import { get } from '../utils/api';
import { Spinner, formatDate } from '../components/Shared';
import { useNavigate } from 'react-router-dom';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

export default function Exceptions() {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const data = await get('/api/invoices/report/exceptions');
      setExceptions(data.exceptions || []);
    } finally { setLoading(false); }
  }, []);

  const lastUpdated = useAutoRefresh(load);

  function exportCSV() {
    const headers = ['Exception Type','Invoice #','Party','Address','Delivery Person','Hours Elapsed','Voucher Outcome'];
    const rows = filtered.map(e => [
      e.exception_type, e.invoice_number, e.party_name, e.party_address,
      e.delivery_person_name, e.hours_since_confirmation, e.voucher_outcome || ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `exceptions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  }

  const filtered = exceptions.filter(e => !typeFilter || e.exception_type === typeFilter);

  // NOTE: exception-type color-coding left untouched — same categorical
  // reasoning as invoice stage colors.
  const TYPE_LABELS = {
    unsigned_voucher:  { label: 'Unsigned Voucher',   color: 'bg-red-100 text-red-700' },
    unreturned_voucher:{ label: 'Not Returned',        color: 'bg-amber-100 text-amber-700' },
    reattempt_overdue: { label: 'Reattempt Overdue',  color: 'bg-orange-100 text-orange-700' },
  };

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Exception Report</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{exceptions.length} total exceptions requiring action</p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              🔄 Auto-refreshing · Last updated {lastUpdated.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', second:'2-digit' })}
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none">
            <option value="">All Types ({exceptions.length})</option>
            <option value="unsigned_voucher">Unsigned Vouchers</option>
            <option value="unreturned_voucher">Not Returned</option>
            <option value="reattempt_overdue">Reattempt Overdue</option>
          </select>
          <button onClick={exportCSV}
            className="px-4 py-2 bg-brand-700 text-white text-sm rounded-lg hover:bg-brand-500 font-medium">
            Export CSV
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-12 text-center">
          <p className="text-4xl mb-3">✅</p>
          <p className="text-green-700 font-semibold">No exceptions! All vouchers are accounted for.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((ex, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="flex justify-between items-center px-5 py-4 bg-gray-50 dark:bg-gray-900 border-b">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${TYPE_LABELS[ex.exception_type]?.color}`}>
                    {TYPE_LABELS[ex.exception_type]?.label}
                  </span>
                  <span className="font-bold text-gray-800 dark:text-gray-100">{ex.invoice_number}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">— {ex.party_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-red-600">
                    {Math.round(ex.hours_since_confirmation)}h elapsed
                  </span>
                  <button onClick={() => navigate(`/invoices/${ex.invoice_id}`)}
                    className="text-xs bg-brand-700 text-white px-3 py-1 rounded-lg hover:bg-brand-500">
                    View Invoice →
                  </button>
                </div>
              </div>
              <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Party Address</p>
                  <p className="text-gray-700 dark:text-gray-300 text-xs">{ex.party_address}</p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">📞 {ex.party_phone}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Delivery Person</p>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">{ex.delivery_person_name || '—'}</p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">{ex.delivery_person_phone || ''}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Confirmed At</p>
                  <p className="text-gray-700 dark:text-gray-300 text-xs">{formatDate(ex.voucher_confirmed_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Voucher Outcome</p>
                  <p className="text-gray-700 dark:text-gray-300 capitalize font-medium">{ex.voucher_outcome || '—'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
